"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import { Button } from "components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "components/ui/card";
import { INsuRegister } from "src/@types/nsu-register";
import { formatToBRL } from "../../../../../utils/formatToMoney";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useGetFilesQuery,
  usePublicGetFilesQuery,
  usePublicUploadFileMutation,
  useUploadFileMutation,
} from "@/services/file";
import { toast } from "components/ui/use-toast";
import { useForm } from "react-hook-form";
import React, { useRef, useState } from "react";
import { cn } from "../../../../../lib/utils";
import { useNsuRegisterFilterActions } from "../../ducks/filter/hooks/actions";
import { Camera, FileIcon, Loader2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { featureFlags } from "@/config/feature-flag";

interface FormData {
  evidence: FileList;
}

interface CardMobileProps extends INsuRegister {}

export default function CardMobile({
  number,
  price,
  id,
  entity_name,
  methods,
}: CardMobileProps) {
  const { handleModal } = useDialogModal();

  const uploadFileMutationPrivate = useUploadFileMutation();
  const uploadFileMutationPublic = usePublicUploadFileMutation();

  const featureFlagsChoice = featureFlags.USE_KEYCLOAK;
  const [uploadFile, { isLoading: isUploading }] = featureFlagsChoice
    ? uploadFileMutationPublic
    : uploadFileMutationPrivate;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { register, handleSubmit, reset, setValue } = useForm<FormData>();
  const { setValue: setInOtherContext } = methods;
  const { updateFilter } = useNsuRegisterFilterActions();
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    name: string;
    type: string;
    file: File;
  } | null>(null);

  React.useEffect(() => {
    setInOtherContext(`divergence_opr.id_${id}`, id);
  }, [id, setInOtherContext]);

  const objectGetFiles = {
    entity_name: entity_name ?? "",
    id: id?.toString() ?? "",
  };

  const getFileKeycloak = featureFlagsChoice
    ? usePublicGetFilesQuery(objectGetFiles)
    : useGetFilesQuery(objectGetFiles);

  const { data: files } = getFileKeycloak;

  const filesData = files?.data ?? [];
  const filesCount = filesData.length;

  React.useEffect(() => {
    setInOtherContext(`divergence_opr.length_file_${id}`, filesCount);
    updateFilter({
      key: `length_file_${id}`,
      value: filesCount,
    });
  }, [filesCount, id, setInOtherContext, updateFilter]);

  const onSubmit = async (data: FormData) => {
    try {
      if (data.evidence && data.evidence.length > 0) {
        const file = data.evidence[0];

        console.log("CardMobile Starting upload for ID:", id);

        await uploadFile({
          file,
          entity_name,
          entity_id: id as number,
        }).unwrap();

        console.log("CardMobile Upload completed for ID:", id);

        await updateFilter({
          key: "store_justification",
          value: id,
        });

        toast({
          variant: "default",
          description: "Evidência enviada com sucesso",
          duration: 5000,
        });

        reset();
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("Failed to upload file:", error);
      toast({
        variant: "destructive",
        description: "Erro ao enviar arquivo",
        duration: 5000,
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setValue("evidence", e.target.files);
      setSelectedFile({
        name: file.name,
        type: file.type,
        file: file,
      });
      if (file.type.startsWith("image/")) {
        setIsFileModalOpen(true);
      } else {
        await handleSubmit(onSubmit)();
      }
    }
  };

  const triggerFileInput = (captureMethod?: "user" | "environment") => {
    if (fileInputRef.current && !isUploading) {
      if (captureMethod) {
        fileInputRef.current.setAttribute("capture", captureMethod);
      } else {
        fileInputRef.current.removeAttribute("capture");
      }
      fileInputRef.current.click();
    }
  };

  const openCamera = () => {
    triggerFileInput("environment");
  };

  React.useEffect(() => {
    return () => {
      if (selectedFile?.file) {
        try {
          URL.revokeObjectURL(URL.createObjectURL(selectedFile.file));
        } catch (error) {
          console.warn("Error cleaning up object URL:", error);
        }
      }
    };
  }, [selectedFile]);

  const closeFileModal = () => {
    if (selectedFile?.file) {
      try {
        URL.revokeObjectURL(URL.createObjectURL(selectedFile.file));
      } catch (error) {
        console.warn("Error cleaning up object URL:", error);
      }
    }
    setSelectedFile(null);
    setIsFileModalOpen(false);
  };

  return (
    <>
      <Card className="w-full border flex flex-col max-w-full">
        <CardHeader className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <p className="font-semibold">Número da NSU:</p>
            <p className="!text-[#444444]">{number}</p>
          </div>
          <div className="flex justify-between items-center">
            <p className="font-semibold">Valor:</p>
            <p className="!text-[#444444]">{formatToBRL(+price)}</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Accordion type="multiple" className="w-full">
            <AccordionItem value="item-2" className="border rounded-[8px] px-4">
              <AccordionTrigger>
                <p className="font-semibold">
                  Evidências ({filesCount})
                </p>
              </AccordionTrigger>
              <AccordionContent className="!text-[#444444]">
                {filesCount > 0 ? (
                  <ul className="space-y-2">
                    {filesData.map((file: any, index: number) => {
                      const fileName = file?.file_name ?? "Arquivo";
                      const fileUrl = file?.file_url ?? "";
                      const fileType = file?.file_type ?? "";
                      const truncatedName =
                        fileName.length > 25
                          ? fileName.substring(0, 25) + "..."
                          : fileName;
                      return (
                        <li
                          key={file?.id ?? index}
                          className="flex items-center gap-2 hover:bg-gray-50 p-2 rounded-md cursor-pointer"
                          onClick={() => {
                            handleModal({
                              isOpen: true,
                              element: (
                                <div className="p-4">
                                  <h3 className="font-semibold mb-4">
                                    {fileName}
                                  </h3>
                                  {fileType.startsWith("image/") ? (
                                    <img
                                      src={fileUrl}
                                      alt={fileName}
                                      className="max-w-full h-auto rounded-lg"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <FileIcon className="h-6 w-6" />
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                      >
                                        Abrir arquivo
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ),
                              title: "Visualizar arquivo",
                            });
                          }}
                        >
                          <FileIcon className="h-4 w-4 text-gray-500" />
                          <span>{truncatedName}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <span>
                    Ops! Esta nsu não está mais disponível. Por favor, entre em
                    contato com o seu tesoureiro e solicite um novo link para
                    resposta.
                  </span>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>

        <CardFooter className="flex justify-between items-center w-full max-w-full flex-col gap-3">
          {isUploading && (
            <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  Enviando evidência...
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-4 w-full justify-center">
            <Button
              variant="outline"
              className={cn(
                "text-[#3C7DD9] font-semibold flex items-center gap-2 transition-all",
                isUploading && "opacity-50 cursor-not-allowed"
              )}
              onClick={openCamera}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
              {isUploading ? "Enviando..." : "Câmera"}
            </Button>

            <Button
              variant="outline"
              className={cn(
                "text-[#3C7DD9] font-semibold flex items-center gap-2 transition-all",
                isUploading && "opacity-50 cursor-not-allowed"
              )}
              onClick={() => triggerFileInput()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileIcon className="h-4 w-4" />
              )}
              {isUploading ? "Enviando..." : "Arquivo"}
            </Button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="hidden">
            <input
              type="file"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              {...register("evidence")}
              onChange={handleFileChange}
              ref={fileInputRef}
              className="hidden"
              disabled={isUploading}
            />
          </form>
        </CardFooter>

        <Dialog
          open={isFileModalOpen}
          onOpenChange={(open) => !open && closeFileModal()}
        >
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Visualizar arquivo</DialogTitle>
              <DialogDescription>{selectedFile?.name}</DialogDescription>
            </DialogHeader>
            <div className="w-full aspect-square relative overflow-hidden rounded-lg">
              {selectedFile?.type.startsWith("image/") &&
                selectedFile?.file && (
                  <img
                    src={URL.createObjectURL(selectedFile.file)}
                    alt="Preview"
                    className="object-contain w-full h-full"
                  />
                )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeFileModal}
                disabled={isUploading}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  handleSubmit(onSubmit)();
                  closeFileModal();
                }}
                disabled={isUploading}
                className="flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Salvar
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Card>
    </>
  );
}
